// Deterministic import of an AI-built ("interchange") plan into Teddo's native
// project shape — no AI round-trip needed.
//
// The downloadable skill (app/skills/time-distance-plan) produces JSON in an
// interchange format: `{ project: {...}, distanceLabels?, flows: [{ name,
// color?, checkpoints: [{ id, name, estimatedDuration, status, distance?,
// subtasks: [string], dependencies: [checkpointId] }] }] }`.
//
// Teddo's native format (Menu → Export JSON) is different: top-level `name`,
// checkpoints carry `time`+`distance` and `subtasks: [{text, done}]`, and
// dependencies are a top-level `[{from, to}]` list of generated ids. This
// module converts the former into the latter so `loadProject()` can consume it.

const VALID_STATUS = new Set(['pending', 'active', 'completed', 'blocked'])
const FLOW_COLORS = ['#1f6feb', '#E08A1E', '#2DA44E', '#8250DF', '#CF222E', '#0969DA', '#BF3989']

const round = (n, d) => {
  const p = 10 ** d
  return Math.round(n * p) / p
}

// True when `data` is an interchange plan rather than a native Teddo project.
// Native exports have a top-level `name` and no `project` wrapper; interchange
// plans nest project metadata under `project` and/or use string dependencies.
export function looksLikeInterchangePlan(data) {
  if (!data || typeof data !== 'object' || !Array.isArray(data.flows)) return false
  if (data.project && typeof data.project === 'object') return true
  for (const f of data.flows) {
    for (const c of f?.checkpoints || []) {
      if (Array.isArray(c?.dependencies) && c.dependencies.some((d) => typeof d === 'string')) return true
      if (Array.isArray(c?.subtasks) && c.subtasks.some((s) => typeof s === 'string')) return true
    }
  }
  return false
}

export function interchangeToProject(input, opts = {}) {
  const idFn = typeof opts.idFn === 'function' ? opts.idFn : () => crypto.randomUUID()
  const src = input || {}
  const proj = (src.project && typeof src.project === 'object') ? src.project : {}

  const labels = Array.isArray(src.distanceLabels)
    && src.distanceLabels.filter((l) => typeof l === 'string' && l.trim()).length >= 2
    ? src.distanceLabels.map((l) => String(l).trim()).filter(Boolean)
    : null
  const distanceRange = ((labels?.length || 5) - 1) || 4
  const timeRange = 12
  const timeStart = 0

  const rawFlows = (Array.isArray(src.flows) ? src.flows : []).filter(
    (f) => f && f.name != null && String(f.name).trim()
  )
  const F = rawFlows.length
  const idByOld = {} // interchange checkpoint id -> new id

  const flows = rawFlows.map((f, fi) => {
    const lane = round(F > 0 ? ((fi + 1) / (F + 1)) * distanceRange : distanceRange / 2, 3)
    const cps = (Array.isArray(f.checkpoints) ? f.checkpoints : []).filter(
      (c) => c && c.name != null && String(c.name).trim()
    )
    const n = cps.length
    return {
      id: idFn(),
      name: String(f.name).trim(),
      color: typeof f.color === 'string' && f.color.trim() ? f.color.trim() : FLOW_COLORS[fi % FLOW_COLORS.length],
      hidden: !!f.hidden,
      checkpoints: cps.map((c, ci) => {
        const newId = idFn()
        if (c.id != null) idByOld[String(c.id)] = newId
        return {
          id: newId,
          name: String(c.name).trim(),
          time: Number.isFinite(c.time) ? c.time : round(timeStart + ((ci + 1) / (n + 1)) * timeRange, 2),
          distance: Number.isFinite(c.distance) ? c.distance : lane,
          status: VALID_STATUS.has(c.status) ? c.status : 'pending',
          type: 'checkpoint',
          estimatedDuration: Number.isFinite(c.estimatedDuration) ? c.estimatedDuration : 0,
          subtasks: (Array.isArray(c.subtasks) ? c.subtasks : [])
            .map((s) => {
              if (typeof s === 'string') return s.trim() ? { text: s.trim(), done: false } : null
              if (s && typeof s.text === 'string' && s.text.trim()) return { text: s.text.trim(), done: !!s.done }
              return null
            })
            .filter(Boolean),
          _deps: (Array.isArray(c.dependencies) ? c.dependencies : []).map(String),
        }
      }),
    }
  })

  // Resolve per-checkpoint dependency ids into top-level {from, to} edges.
  // A checkpoint's `dependencies` list names checkpoints that must finish
  // first, so the edge is dep -> checkpoint (from = dependency).
  const seen = new Set()
  const dependencies = []
  for (const f of flows) {
    for (const c of f.checkpoints) {
      for (const oldDep of c._deps) {
        const from = idByOld[oldDep]
        if (from && from !== c.id) {
          const key = `${from}|${c.id}`
          if (!seen.has(key)) {
            seen.add(key)
            dependencies.push({ from, to: c.id })
          }
        }
      }
      delete c._deps
    }
  }

  return {
    name: (proj.name && String(proj.name).trim()) || 'Imported plan',
    description: proj.description ? String(proj.description) : '',
    ...(proj.startDate ? { startDate: String(proj.startDate) } : {}),
    ...(labels ? { distanceLabels: labels } : {}),
    flows,
    dependencies,
  }
}
